import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowTopRightOnSquareIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import useSearchInput from '@/dashboard/hooks/useSearchInput';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import { pagesKey } from '@/dashboard/lib/queryKeys';
import { formatEventDate } from '@/dashboard/lib/format-date';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { Label } from '@/dashboard/components/Form';
import { Command, CommandEmpty, CommandItem, CommandList } from '@dashboard/components/ui/command';
import { Popover, PopoverAnchor, PopoverContent } from '@dashboard/components/ui/popover';
import { Input } from '@dashboard/components/ui/input';
import { Skeleton } from '@dashboard/components/ui/skeleton';

import PageMultiSelectPageContainer from './PageMultiSelectPageContainer';

export const openDashboardLink = pageId => {
  window.open(`/dashboard/pages/${pageId}/customize/product`, '_blank');
};

// One search result, shared with the coupon picker so both lists look the same.
export const PageSearchItem = ({ page, onSelect, showType = true }) => (
  <CommandItem value={page._id} onSelect={() => onSelect(page)} className="flex items-center gap-2">
    <div className="min-w-0 flex-1">
      {showType && <div className="text-xs text-muted-foreground capitalize">{page.type}</div>}
      <div className="truncate text-sm font-semibold">{page.name}</div>
      {page.type === 'checkout' && (
        <div className="text-sm text-muted-foreground">
          <span>{getProductPriceLabel(page.product)}</span>
        </div>
      )}
      {page.type === 'event' && page.eventDetails?.startDate && (
        <div className="text-xs text-muted-foreground">
          <div className="flex items-start gap-x-1.5">
            <CalendarIcon className="mt-px !size-3.5 shrink-0" />
            <div className="flex flex-col">
              {formatEventDate({
                startDate: page.eventDetails.startDate,
                endDate: page.eventDetails.endDate,
                timezone: page.eventDetails.timezone,
                locale: page.locale,
              }).map(date => (
                <span key={date}>{date}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>

    <button
      type="button"
      onMouseDown={e => e.preventDefault()}
      onClick={e => {
        e.stopPropagation();
        openDashboardLink(page._id);
      }}
      className="ml-auto text-muted-foreground transition-colors hover:text-foreground"
      aria-label="Open page in new tab"
    >
      <ArrowTopRightOnSquareIcon className="size-4" />
    </button>
  </CommandItem>
);

export const PageSearchSkeleton = () =>
  Array.from({ length: 5 }).map((_, i) => (
    <div key={i} aria-hidden="true" className="flex items-center gap-2 rounded-md px-1.5 py-1">
      <div className="min-w-0 flex-1">
        <div className="flex h-4 items-center">
          <Skeleton className="h-3 w-12" />
        </div>
        <div className="flex h-5 items-center">
          <Skeleton className="h-3.5 w-40" />
        </div>
      </div>
      <Skeleton className="size-4 shrink-0 rounded-md" />
    </div>
  ));

const PageMultiSelect = ({
  placeholder = 'Filter by page',
  isSingle = false,
  label,
  helpText,
  name,
  onChange,
  pageTypes = ['checkout', 'event'],
  productTypes = [],
  initialValue,
  includeTicketTypes = false,
}) => {
  const { getPages } = usePageQueries();
  const { sellerId } = useAuth();
  const inputRef = useRef(null);
  const hasInitialized = useRef(false);
  const [open, setOpen] = useState(false);
  const [selectedPages, setSelectedPages] = useState(new Map());
  const [pageTicketSelections, setPageTicketSelections] = useState(new Map());
  const { searchInput, setSearchInput, search } = useSearchInput('');

  const initialPageIds =
    initialValue && Array.isArray(initialValue) ? initialValue.map(v => v.pageId) : [];

  const { data: initialPagesData } = useQuery({
    queryKey: ['initial-pages', initialPageIds],
    queryFn: () =>
      getPages(sellerId, {
        status: ['published', 'draft'],
        type: pageTypes,
        productType: productTypes,
        _id: initialPageIds,
      }),
    enabled: initialPageIds.length > 0 && selectedPages.size === 0,
  });

  const { data, fetchNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: [pagesKey, search, pageTypes, productTypes],
    queryFn: async ({ pageParam }) =>
      await getPages(sellerId, {
        status: ['published', 'draft'],
        type: pageTypes,
        productType: productTypes,
        limit: 10,
        skip: pageParam * 10,
        search: search === '' ? undefined : search,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
  });

  const addSelectedPage = useCallback(pageToAdd => {
    setSelectedPages(prev => {
      if (prev.has(pageToAdd._id)) return prev;
      const newMap = new Map(prev);
      newMap.set(pageToAdd._id, { ...pageToAdd });
      return newMap;
    });
  }, []);

  const clear = useCallback(() => {
    setSelectedPages(new Map());
    setPageTicketSelections(new Map());
    setSearchInput('');
  }, [setSearchInput]);

  const removeSelectedPage = pageToRemove => {
    setSelectedPages(prev => {
      const newMap = new Map(prev);
      newMap.delete(pageToRemove._id);
      return newMap;
    });

    setPageTicketSelections(prev => {
      const newMap = new Map(prev);
      newMap.delete(pageToRemove._id);
      return newMap;
    });
  };

  const filteredPages =
    data?.pages?.map(group => ({
      ...group,
      data: group.data.filter(page => !selectedPages.has(page._id)),
    })) ?? [];

  const hasItems = filteredPages?.[0]?.data?.length > 0;

  const handleSelect = page => {
    if (isSingle) {
      setSelectedPages(new Map());
      setPageTicketSelections(new Map());
    }
    addSelectedPage(page);
    setSearchInput('');
    setOpen(false);
  };

  const handleClear = e => {
    e.preventDefault();
    e.stopPropagation();
    clear();
    inputRef.current?.focus();
  };

  const handleTicketTypeSelectionChange = useCallback((pageId, selectedTicketTypeIds) => {
    setPageTicketSelections(prev => {
      if (JSON.stringify(prev.get(pageId)) === JSON.stringify(selectedTicketTypeIds)) {
        return prev;
      }

      const newMap = new Map(prev);
      newMap.set(pageId, selectedTicketTypeIds);
      return newMap;
    });
  }, []);

  useEffect(() => {
    if (initialPagesData?.data && selectedPages.size === 0 && hasInitialized.current === false) {
      const newSelectedPages = new Map();
      const newPageTicketSelections = new Map();

      initialPagesData.data.forEach(page => {
        const initialItem = initialValue.find(item => item.pageId === page._id);
        if (initialItem) {
          newSelectedPages.set(page._id, page);
          if (initialItem.ticketTypeIds && initialItem.ticketTypeIds.length > 0) {
            newPageTicketSelections.set(page._id, initialItem.ticketTypeIds);
          }
        }
      });

      setSelectedPages(newSelectedPages);
      setPageTicketSelections(newPageTicketSelections);
      hasInitialized.current = true;
    }
  }, [initialPagesData, selectedPages.size, initialValue]);

  useEffect(() => {
    onChange({
      pages: selectedPages,
      tickets: pageTicketSelections,
    });
  }, [selectedPages, pageTicketSelections, onChange]);

  const showSingleSelectionInInput = isSingle && selectedPages.size === 1;
  const singleSelectedPage = showSingleSelectionInInput
    ? Array.from(selectedPages.values())[0]
    : null;
  const inputValue = showSingleSelectionInInput ? singleSelectedPage?.name : searchInput;

  return (
    <div>
      {label && <Label>{label}</Label>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              name={name}
              value={inputValue ?? ''}
              placeholder={placeholder}
              autoComplete="off"
              aria-label={placeholder}
              onChange={e => {
                if (showSingleSelectionInInput) {
                  clear();
                }
                setSearchInput(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              className="pr-16"
            />
            <div className="absolute inset-y-0 right-2 flex items-center gap-1 pointer-events-none">
              {(inputValue?.length > 0 || selectedPages.size > 0) && (
                <button
                  type="button"
                  onMouseDown={handleClear}
                  className="text-muted-foreground transition-colors hover:text-foreground pointer-events-auto"
                  aria-label="Clear selection"
                >
                  <XMarkIcon className="size-4" />
                </button>
              )}
              {showSingleSelectionInInput ? (
                <ChevronDownIcon className="size-4 text-muted-foreground" />
              ) : (
                <MagnifyingGlassIcon className="size-4 text-muted-foreground/60" />
              )}
            </div>
          </div>
        </PopoverAnchor>

        <PopoverContent
          // When rendered inside a modal dialog, portal into the dialog content
          // instead of document.body: the dialog's scroll lock blocks wheel/touch
          // scrolling on anything portaled outside it, making the list unscrollable.
          container={inputRef.current?.closest('[role="dialog"]') ?? undefined}
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={e => e.preventDefault()}
          onInteractOutside={e => {
            if (inputRef.current && inputRef.current.contains(e.target)) {
              e.preventDefault();
            }
          }}
        >
          <Command shouldFilter={false}>
            <CommandList className="max-h-72 p-1 scrollbar-hide">
              {isError && <CommandEmpty>Error loading pages</CommandEmpty>}
              {!isError && !isLoading && !hasItems && <CommandEmpty>No pages found</CommandEmpty>}

              <BottomScrollListener onBottom={fetchNextPage} isLoadingMore={isLoading}>
                {filteredPages?.map(group =>
                  group?.data?.map(page => (
                    <PageSearchItem key={page._id} page={page} onSelect={handleSelect} />
                  )),
                )}
              </BottomScrollListener>

              {isLoading && <PageSearchSkeleton />}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {helpText && <div className="block text-muted-foreground text-xs mt-1">{helpText}</div>}

      {!isSingle && selectedPages.size > 0 && (
        <div className="mt-2 flex flex-col gap-2">
          {Array.from(selectedPages.values()).map(page => (
            <PageMultiSelectPageContainer
              key={page._id}
              page={page}
              onRemove={removeSelectedPage}
              onVisit={openDashboardLink}
              showRemoveButton={true}
              includeTicketTypes={includeTicketTypes}
              selectedTicketIds={pageTicketSelections.get(page._id) || []}
              onTicketTypeChange={selectedTicketIds => {
                handleTicketTypeSelectionChange(page._id, selectedTicketIds);
              }}
              isReadOnly={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PageMultiSelect;
